import { hasValue } from 'utils/hasValue';

import type {
  CreditorKeyValuePair,
  CreditorType,
  CreditorWithDetails
} from 'api';

import type { DraftSteps } from './types';

import type { DraftStepPayload } from '../contexts/DraftProgressProvider';

export const extractReceiverInformation = (
  data: Partial<Record<DraftSteps, DraftStepPayload>>
) => {
  if (!hasValue(data.creditor)) {
    return null;
  }

  const creditorAccountDetails =
    data.creditor_account &&
    transformCreditorDetails('creditor_account', data.creditor_account);

  const intermediaryAgentDetails =
    data.intermediary_agent &&
    transformCreditorDetails('intermediary_agent', data.intermediary_agent);

  const intermediaryAgentAccountDetails =
    data.intermediary_agent_account &&
    transformCreditorDetails(
      'intermediary_agent_account',
      data.intermediary_agent_account
    );

  const creditorDetails: CreditorKeyValuePair[] = (creditorAccountDetails || [])
    .concat(intermediaryAgentDetails || [])
    .concat(intermediaryAgentAccountDetails || []);

  const creditorType: CreditorType = data.creditor.type?.['value'].toString();
  const validCreditorType = ['organization', 'private'].includes(creditorType)
    ? creditorType
    : undefined;

  // There's a small deviation between the CreditorWithDetails model and fields we want to extract
  // For instance, the "full_name" is replaced with "name"
  const fieldsToExtract: (keyof CreditorWithDetails | 'name')[] = [
    'address_lines',
    'birthdate',
    'city',
    'country_code',
    'country_of_birth_code',
    'country_of_residence_code',
    'creditor_type',
    'email',
    'name',
    'id_expiry_date',
    'id_issuing_country_code',
    'id_issuing_date',
    'id_number',
    'id_type',
    'mobile_number',
    'postcode',
    'street_name',
    'state'
  ];

  return {
    ...extractCreditorFields(data.creditor, fieldsToExtract),
    creditor_type: validCreditorType,
    creditor_details: creditorDetails
  };
};

/**
 * Internal
 */

const extractCreditorFields = (data: DraftStepPayload, fields: string[]) => {
  return fields.reduce(
    (acc, field) => {
      const value = data[field] as { toString: () => string };
      if (value !== undefined) {
        if (field === 'name') {
          acc['full_name'] = value.toString();
        } else {
          acc[field] = value.toString();
        }
      }
      return acc;
    },
    {} as Record<string, string>
  );
};

const transformCreditorDetails = (
  section: string,
  details: DraftStepPayload
) => {
  return Object.entries(details).map(([key, value]) => ({
    key: `${section}.${key}`,
    value: (value as { toString: () => string }).toString()
  }));
};
